import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { PageHeader, Card, Button, Badge, LoadingState } from '../components/ui';
import api from '../services/api';
import GlobalMessagesFeed from '../components/Agents/GlobalMessagesFeed';
import UnassignedActivityQueue from '../components/Agents/UnassignedActivityQueue';
import AgentModal from '../components/Agents/AgentModal';
import AgentsStatsPanel from '../components/Agents/AgentsStatsPanel';
import { getUnassignedActivityReviews } from '../services/activityReviewService';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  PencilIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

const parseTextareaUsernames = (value) =>
  String(value || '')
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);

const AgentsGlobal = () => {
  const [activeView, setActiveView] = useState('directory');
  const [searchTerm, setSearchTerm] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [lastImport, setLastImport] = useState(null);
  const [lastBackfill, setLastBackfill] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const queryClient = useQueryClient();

  const { data: unassignedCountData } = useQuery(
    ['unassignedActivityReviewCount'],
    () => getUnassignedActivityReviews({ limit: 1, offset: 0 }),
    { refetchOnMount: 'always', refetchInterval: 30000 },
  );

  const { data, isLoading, error } = useQuery(
    ['agents', searchTerm],
    () =>
      api
        .get('/agents', {
          params: {
            search: searchTerm || undefined,
            limit: 200,
            friendlyOnly: true,
          },
        })
        .then((res) => res.data),
    {
      enabled: activeView === 'directory',
      keepPreviousData: true,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    }
  );

  // Global directory stats (independent of search/filter applied to the table).
  // Refreshed whenever a bulk-import or backfill mutation succeeds so the
  // summary card tracks the live totals, not the page-load snapshot.
  const {
    data: statsData,
    isLoading: isStatsLoading,
  } = useQuery(
    ['agentStats'],
    () =>
      api
        .get('/agents/stats', { params: { friendlyOnly: true } })
        .then((res) => res.data?.stats || null),
    {
      enabled: activeView === 'directory',
      keepPreviousData: true,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
    }
  );

  const deleteAgentMutation = useMutation(
    (id) => api.delete(`/agents/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('agents');
        queryClient.invalidateQueries('agentStats');
      },
    }
  );

  const bulkImportMutation = useMutation(
    (text) =>
      api
        .post('/agents/bulk-import-usernames', {
          text,
          timeoutMs: 8000,
          delayMs: 150,
          limit: 500,
        })
        .then((res) => res.data),
    {
      onSuccess: (result) => {
        setLastImport(result);
        setBulkText('');
        queryClient.invalidateQueries('agents');
        queryClient.invalidateQueries('agentStats');
        queryClient.invalidateQueries('globalAgentMessages');
      },
    }
  );

  const backfillMutation = useMutation(
    ({ limit, owner } = {}) =>
      api
        .post('/agents/backfill-unresolved', {
          limit: limit || 50,
          delayMs: 200,
          candidateLimit: 3,
          timeoutMs: 8000,
          owner: owner || null,
        })
        .then((res) => res.data),
    {
      onSuccess: (result) => {
        const summary = result?.summary || result || {};
        setLastBackfill(summary);
        queryClient.invalidateQueries('agents');
        queryClient.invalidateQueries('agentStats');
        queryClient.invalidateQueries('agentOwners');
        queryClient.invalidateQueries('globalAgentMessages');
      },
    }
  );

  const agents = data?.agents || [];
  const usernamesToImport = parseTextareaUsernames(bulkText);

  if (activeView === 'directory' && isLoading) return <LoadingState />;
  if (activeView === 'directory' && error) return <div className="text-red-500">Error: {error.message}</div>;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Friendly Agents"
        description={
          activeView === 'directory'
            ? 'Stage 1: add our Telegram agents by username, resolve user_id, and track every message they send.'
            : activeView === 'messages'
              ? 'Global feed of friendly-agent messages, with tracked channel discussions labelled separately'
              : 'Completed activity chains that still need one project assignment'
        }
        actions={
          activeView === 'directory' ? (
            <Button variant="primary" onClick={() => setShowAddModal(true)}>
              <PlusIcon className="h-5 w-5 mr-2" />
              Add one
            </Button>
          ) : null
        }
      />

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-8">
          {[
            { id: 'directory', label: 'Agents' },
            { id: 'messages', label: 'Messages' },
            { id: 'needs-project', label: `Needs project · ${unassignedCountData?.total || 0}` },
          ].map((view) => (
            <button
              key={view.id}
              onClick={() => setActiveView(view.id)}
              className={`border-b-2 px-1 py-3 text-sm font-medium ${
                activeView === view.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              {view.label}
            </button>
          ))}
        </nav>
      </div>

      {activeView === 'directory' && (
        <>
          <AgentsStatsPanel stats={statsData} isLoading={isStatsLoading} />

          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between gap-4">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Bulk add by usernames</h2>
                <p className="text-xs text-gray-500">
                  Paste one per line: <code>@username Owner</code>. Backend saves owner, resolves user_id, and pulls profile data immediately.
                </p>
              </div>
              <Button
                variant="primary"
                disabled={usernamesToImport.length === 0 || bulkImportMutation.isLoading}
                onClick={() => bulkImportMutation.mutate(bulkText)}
              >
                {bulkImportMutation.isLoading ? 'Importing...' : `Import ${usernamesToImport.length || ''}`}
              </Button>
            </div>
            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              rows={5}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              placeholder={'@username1 Люба\nhttps://t.me/username2 Ханум\n@username3 Люба Турция'}
            />
            {bulkImportMutation.error && (
              <div className="mt-2 text-sm text-red-600">
                {bulkImportMutation.error.response?.data?.error || bulkImportMutation.error.message}
              </div>
            )}
            {lastImport?.summary && (
              <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
                <span className="font-medium text-gray-900">Last import:</span>{' '}
                received {lastImport.summary.received}, created {lastImport.summary.created}, updated {lastImport.summary.updated},
                resolved {lastImport.summary.resolved}, unresolved {lastImport.summary.unresolved}, invalid {lastImport.summary.invalid}.
                {!lastImport.summary.runtime_available && (
                  <span className="ml-1 text-amber-700">No runtime TDLib client was available, saved as unresolved.</span>
                )}
              </div>
            )}
          </Card>

          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between gap-4">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Resolve pending / half-resolved</h2>
                <p className="text-xs text-gray-500">
                  Run TDLib again over friendly agents missing user_id, or with a user_id but no profile.
                  Distributes lookups across the runtime pool; one batch is bounded to keep Telegram happy.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={(backfillMutation.variables?.limit || 50)}
                  onChange={(e) => backfillMutation.mutate({ limit: parseInt(e.target.value, 10) })}
                  className="rounded-lg border border-gray-300 px-2 py-2 text-xs focus:ring-2 focus:ring-blue-500"
                  disabled={backfillMutation.isLoading}
                >
                  {[25, 50, 100, 200, 500].map((n) => (
                    <option key={n} value={n}>{`Batch ${n}`}</option>
                  ))}
                </select>
                <Button
                  variant="secondary"
                  disabled={backfillMutation.isLoading}
                  onClick={() => backfillMutation.mutate({ limit: backfillMutation.variables?.limit || 50 })}
                >
                  {backfillMutation.isLoading ? 'Resolving…' : 'Resolve pending'}
                </Button>
              </div>
            </div>
            {backfillMutation.error && (
              <div className="mt-2 text-sm text-red-600">
                {backfillMutation.error.response?.data?.error || backfillMutation.error.message}
              </div>
            )}
            {lastBackfill && (
              <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
                <span className="font-medium text-gray-900">Last backfill:</span>{' '}
                scanned {lastBackfill.scanned}, re-resolved {lastBackfill.reResolved},
                {' '}profile fetched {lastBackfill.enriched}, profile failed {lastBackfill.profileFetchFailed},
                {' '}still unresolved {lastBackfill.stillUnresolved}.
                {!lastBackfill.runtime_available && (
                  <span className="ml-1 text-amber-700">No runtime TDLib client was available.</span>
                )}
                {Array.isArray(lastBackfill.owners_resolved) && lastBackfill.owners_resolved.length > 0 && (
                  <span className="ml-1">Owners touched: {lastBackfill.owners_resolved.join(', ')}.</span>
                )}
              </div>
            )}
          </Card>

          <Card>
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by username or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <p className="mt-2 text-xs text-gray-500">Showing {agents.length} friendly agents</p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Owner</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Messages</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last message</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {agents.map((agent) => (
                    <tr key={agent.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/agents/${agent.id}`} className="text-blue-600 font-medium hover:underline">
                          @{agent.username}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(agent.first_name || agent.last_name) ? `${agent.first_name || ''} ${agent.last_name || ''}`.trim() : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                        {agent.owner ? (
                          <span>
                            {agent.owner}
                            {agent.owner_region ? <span className="text-gray-400"> / {agent.owner_region}</span> : null}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500">{agent.user_id || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900">{agent.total_messages || 0}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                        {agent.last_message_at ? new Date(agent.last_message_at).toLocaleString() : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={agent.user_id ? 'green' : 'yellow'}>
                          {agent.user_id ? 'Resolved' : 'Unresolved'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <Link to={`/agents/${agent.id}`} className="text-gray-500 hover:text-blue-600 mr-3">
                          <EyeIcon className="h-5 w-5 inline" />
                        </Link>
                        <button
                          onClick={() => setEditingAgent(agent)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          <PencilIcon className="h-5 w-5 inline" />
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Deactivate @${agent.username}?`)) {
                              deleteAgentMutation.mutate(agent.id);
                            }
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-5 w-5 inline" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {agents.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                        No friendly agents yet. Paste usernames above to start tracking.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {activeView === 'messages' && <GlobalMessagesFeed />}
      {activeView === 'needs-project' && <UnassignedActivityQueue />}

      {showAddModal && (
        <AgentModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            queryClient.invalidateQueries('agents');
            queryClient.invalidateQueries('agentStats');
          }}
        />
      )}

      {editingAgent && (
        <AgentModal
          agent={editingAgent}
          onClose={() => setEditingAgent(null)}
          onSuccess={() => {
            setEditingAgent(null);
            queryClient.invalidateQueries('agents');
            queryClient.invalidateQueries('agentStats');
          }}
        />
      )}
    </div>
  );
};

export default AgentsGlobal;
